// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { getReferenceString } from '@medplum/core';
import type {
  Appointment,
  ChargeItem,
  Claim,
  ClinicalImpression,
  Encounter,
  Patient,
  PlanDefinitionAction,
  Practitioner,
  Reference,
  Task,
} from '@medplum/fhirtypes';
import { useMedplum, useResource } from '@medplum/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getChargeItemsForEncounter } from '../utils/chargeitems';
import { showErrorNotification } from '../utils/notifications';

export interface ChecklistItem {
  id: string;
  label: string;
  task?: Task;
}

export interface EncounterChartHook {
  // State values
  encounter: Encounter | undefined;
  claim: Claim | undefined;
  practitioner: Practitioner | undefined;
  tasks: Task[];
  checklistItems: ChecklistItem[];
  clinicalImpression: ClinicalImpression | undefined;
  clinicalImpressionLoaded: boolean;
  chargeItems: ChargeItem[];
  appointment: Appointment | undefined;
  // State setters
  setEncounter: React.Dispatch<React.SetStateAction<Encounter | undefined>>;
  setClaim: React.Dispatch<React.SetStateAction<Claim | undefined>>;
  setPractitioner: React.Dispatch<React.SetStateAction<Practitioner | undefined>>;
  setTasks: React.Dispatch<React.SetStateAction<Task[]>>;
  setClinicalImpression: React.Dispatch<React.SetStateAction<ClinicalImpression | undefined>>;
  setChargeItems: React.Dispatch<React.SetStateAction<ChargeItem[]>>;
  setAppointment: React.Dispatch<React.SetStateAction<Appointment | undefined>>;
}

export function useEncounterChart(
  encounter: Encounter | Reference<Encounter> | undefined,
  patient?: Patient | Reference<Patient>
): EncounterChartHook {
  const medplum = useMedplum();
  const encounterResource = useResource(encounter);
  const [encounterState, setEncounter] = useState<Encounter | undefined>(encounterResource);
  const [claim, setClaim] = useState<Claim | undefined>();
  const [practitioner, setPractitioner] = useState<Practitioner | undefined>();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [planDefinitionActions, setPlanDefinitionActions] = useState<PlanDefinitionAction[]>([]);
  const [clinicalImpression, setClinicalImpression] = useState<ClinicalImpression | undefined>();
  const [clinicalImpressionLoaded, setClinicalImpressionLoaded] = useState(false);
  const [chargeItems, setChargeItems] = useState<ChargeItem[]>([]);
  const [appointment, setAppointment] = useState<Appointment | undefined>();

  const encounterToUse = encounterState ?? encounterResource;

  // Initialize local encounter state when the resource first resolves from the server.
  // This handles the async case where useResource returns undefined on first render.
  useEffect(() => {
    if (encounterResource && !encounterState) {
      setEncounter(encounterResource);
    }
  }, [encounterResource, encounterState]);

  useEffect(() => {
    async function loadChargeItems(): Promise<void> {
      if (encounterResource) {
        const chargeItemsResult = await getChargeItemsForEncounter(medplum, encounterResource);
        setChargeItems(chargeItemsResult);
      }
    }

    async function fetchClaim(): Promise<void> {
      if (!encounterResource?.id) {
        return;
      }
      const response = await medplum.searchResources('Claim', `encounter=${getReferenceString(encounterResource)}`);
      if (response.length !== 0) {
        setClaim(response[0]);
      }
    }

    loadChargeItems().catch(showErrorNotification);
    fetchClaim().catch(showErrorNotification);
  }, [encounterResource, medplum]);

  // Fetch tasks and PlanDefinition actions for the encounter checklist.
  // Strategy: find the CarePlan created by PlanDefinition/$apply, read its
  // instantiatesCanonical to get the PlanDefinition, load tasks via basedOn.
  // Falls back to Task?encounter= when no CarePlan exists.
  const fetchTasks = useCallback(async (): Promise<void> => {
    if (!encounterResource) {
      return;
    }

    const carePlans = await medplum.searchResources(
      'CarePlan',
      `encounter=${getReferenceString(encounterResource)}&_count=1`,
      { cache: 'no-cache' }
    );

    let taskResult: Task[];

    if (carePlans.length > 0) {
      const carePlan = carePlans[0];
      taskResult = await medplum.searchResources('Task', `based-on=CarePlan/${carePlan.id}`, { cache: 'no-cache' });

      const pdRef = carePlan.instantiatesCanonical?.[0];
      if (pdRef) {
        try {
          const pd = pdRef.startsWith('PlanDefinition/')
            ? await medplum.readResource('PlanDefinition', pdRef.replace('PlanDefinition/', ''))
            : (await medplum.searchResources('PlanDefinition', `url=${pdRef}`))[0];
          setPlanDefinitionActions(pd?.action ?? []);
        } catch {
          setPlanDefinitionActions([]);
        }
      } else {
        setPlanDefinitionActions([]);
      }
    } else {
      taskResult = await medplum.searchResources('Task', `encounter=${getReferenceString(encounterResource)}`, {
        cache: 'no-cache',
      });
      setPlanDefinitionActions([]);
    }

    taskResult.sort((a: Task, b: Task) => {
      const dateA = new Date(a.authoredOn || '').getTime();
      const dateB = new Date(b.authoredOn || '').getTime();
      return dateA - dateB;
    });

    setTasks(taskResult);
  }, [medplum, encounterResource]);

  // checklistItems is derived from planDefinitionActions + tasks so it stays
  // in sync whenever tasks are updated (e.g. after a toggle).
  const checklistItems = useMemo<ChecklistItem[]>(() => {
    if (planDefinitionActions.length > 0) {
      return planDefinitionActions.map((action) => ({
        id: action.id ?? action.title ?? '',
        label: action.title ?? action.description ?? action.id ?? '',
        task: tasks.find((t) => t.code?.coding?.some((c) => c.code === action.id)),
      }));
    }
    return tasks.map((t) => ({
      id: t.id ?? '',
      label: t.description ?? t.code?.text ?? t.id ?? '',
      task: t,
    }));
  }, [planDefinitionActions, tasks]);

  // Fetch clinical impressions related to the encounter
  const fetchClinicalImpressions = useCallback(async (): Promise<void> => {
    if (!encounterResource) {
      return;
    }
    const clinicalImpressionResult = await medplum.searchResources(
      'ClinicalImpression',
      `encounter=${getReferenceString(encounterResource)}`,
      { cache: 'no-cache' }
    );

    const result = clinicalImpressionResult?.[0];
    setClinicalImpression(result);
    setClinicalImpressionLoaded(true);
  }, [medplum, encounterResource]);

  // Fetch data on component mount or when encounter changes
  useEffect(() => {
    if (encounterResource) {
      fetchTasks().catch((err) => showErrorNotification(err));
      fetchClinicalImpressions().catch((err) => showErrorNotification(err));
    }
  }, [encounterResource, fetchTasks, fetchClinicalImpressions]);

  // Fetch practitioner related to the encounter
  useEffect(() => {
    const fetchPractitioner = async (): Promise<void> => {
      if (encounterResource?.participant?.[0]?.individual) {
        const practitionerResult = await medplum.readReference(encounterResource.participant[0].individual);
        setPractitioner(practitionerResult as Practitioner);
      }
    };

    if (encounterResource) {
      fetchPractitioner().catch((err) => showErrorNotification(err));
    }
  }, [encounterResource, medplum]);

  // Fetch appointment related to the encounter
  useEffect(() => {
    const fetchAppointment = async (): Promise<void> => {
      const appointmentRef = encounterResource?.appointment?.at(-1);
      if (appointmentRef) {
        const appointmentResult = await medplum.readReference(appointmentRef);
        setAppointment(appointmentResult as Appointment);
      }
    };

    if (encounterResource) {
      fetchAppointment().catch((err) => showErrorNotification(err));
    }
  }, [encounterResource, medplum]);

  return {
    // State values - use resolved resource when available
    encounter: encounterToUse,
    claim,
    practitioner,
    tasks,
    checklistItems,
    clinicalImpression,
    clinicalImpressionLoaded,
    chargeItems,
    appointment,
    // State setters
    setEncounter,
    setClaim,
    setPractitioner,
    setTasks,
    setClinicalImpression,
    setChargeItems,
    setAppointment,
  };
}
